import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  InputSignal,
  OnDestroy,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { WebMPreloadService } from '@app/shared/services/web-mpreload.service';

// Definimos los posibles estados del componente para un manejo más robusto.
type VideoStatus = 'loading' | 'loaded' | 'error';
 
/**
 * Componente standalone para renderizar videos WebM de forma optimizada.
 * Utiliza lazy loading, ofrece fallbacks y se integra con un servicio de precarga.
 */
@Component({
  selector: 'app-webm-player',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './web-mplayer.component.html',
  styleUrls: ['./web-mplayer.component.scss'],
})
export class WebMPlayerComponent implements OnDestroy {
  // --- INYECCIÓN DE SERVICIOS ---
  private preloadService = inject(WebMPreloadService);

  // --- INPUTS (PROPIEDADES) ---
  // Usamos input.required para asegurar que la fuente siempre sea provista.
  src: InputSignal<string> = input.required<string>();
  fallbackSrc: InputSignal<string | undefined> = input<string | undefined>(
    undefined
  );
  width: InputSignal<string | number | undefined> = input<
    string | number | undefined
  >('100%');
  height: InputSignal<string | number | undefined> = input<
    string | number | undefined
  >('auto');
  alt: InputSignal<string> = input<string>('Video loop de fondo');
  cssClass: InputSignal<string> = input<string>('');

  // --- OUTPUTS (EVENTOS) ---
  @Output() videoLoaded = new EventEmitter<void>();
  @Output() videoError = new EventEmitter<ErrorEvent>();

  // --- SIGNALS INTERNOS ---
  status: WritableSignal<VideoStatus> = signal<VideoStatus>('loading');

  // Signal computada que decide qué URL usar: la precargada o la original.
  videoSrc = computed(() => {
    const originalSrc = this.src();
    return this.preloadService.getPreloadedUrl(originalSrc) ?? originalSrc;
  });

  private isPreloaded: boolean = false;

  constructor() {
    // Effect que se ejecuta cuando `src` cambia para manejar la lógica de precarga.
    effect(() => {
      const originalSrc = this.src();
      this.isPreloaded = !!this.preloadService.getPreloadedUrl(originalSrc);
    });
  }

  @ViewChild('videoPlayer') videoPlayerRef!: ElementRef<HTMLVideoElement>;

  // --- MANEJADORES DE EVENTOS DEL TEMPLATE ---
  onVideoLoaded(): void {
    this.status.set('loaded');
    this.videoLoaded.emit();

    // Forzamos la reproducción mediante código para máxima compatibilidad.
    this.videoPlayerRef.nativeElement.muted = true; // Aseguramos que el video esté silenciado
    const playPromise = this.videoPlayerRef.nativeElement.play();

    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        // Esto ocurre si el navegador bloquea el autoplay a pesar de todo.
        console.error(
          '[WebMPlayer] La reproducción automática fue prevenida:',
          error
        );
        this.onVideoError(error as Event);
      });
    }
  }

  onVideoError(event: Event): void {
    console.error('Error al cargar el video:', this.src(), event);
    this.status.set('error');
    this.videoError.emit(event as ErrorEvent);
  }

  // --- CICLO DE VIDA ---
  /**
   * Limpia los recursos al destruir el componente.
   * Si el video fue precargado, revocamos la Object URL para liberar memoria.
   */
  ngOnDestroy(): void {
    if (this.isPreloaded) {
      this.preloadService.revokeUrl(this.src());
    }
  }
}
